import { Link } from 'react-router-dom'
import { useMetro } from '../context/MetroContext'
import { BannerAd, AD_SLOTS } from '../components/ads'

const quickActions = [
  {
    title: 'Route Planner',
    description: 'Find the best route between stations',
    icon: '🚇',
    to: '/route',
    color: 'border-metro-blue',
    bg: 'bg-metro-blue-soft',
  },
  {
    title: 'Fare Calculator',
    description: 'Calculate fare with smart card discount',
    icon: '💰',
    to: '/fare',
    color: 'border-metro-green',
    bg: 'bg-metro-green-soft',
  },
  {
    title: 'Metro Map',
    description: 'Interactive metro network map',
    icon: '🗺️',
    to: '/map',
    color: 'border-metro-violet',
    bg: 'bg-metro-violet-soft',
  },
  {
    title: 'All Stations',
    description: 'Browse all metro stations',
    icon: '📍',
    to: '/stations',
    color: 'border-metro-yellow',
    bg: 'bg-metro-yellow-soft',
  },
]

const nearbyOptions = [
  { title: 'Tourist Places', icon: '🏛️', to: '/nearby/tourist' },
  { title: 'Hotels', icon: '🏨', to: '/nearby/hotels' },
  { title: 'Bus Stops', icon: '🚌', to: '/nearby/bus' },
  { title: 'Smart Card', icon: '💳', to: '/smart-card' },
]

function Home() {
  const { selectedCity, currentCity, cities, stations, lines } = useMetro()

  const totalStations = cities.reduce((sum, c) => sum + c.stationCount, 0)
  const totalLines = cities.reduce((sum, c) => sum + c.lineCount, 0)
  const operationalCities = cities.filter(c => c.isOperational).length

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-hero py-16 lg:py-24">
        <div className="container-responsive text-center">
          <h1 className="text-4xl lg:text-5xl xl:text-6xl 2xl:text-tv-3xl font-bold text-neutral-dark mb-6">
            {currentCity ? currentCity.metroName : 'Indian Metro Web'}
          </h1>
          <p className="text-lg lg:text-xl 2xl:text-tv-lg text-neutral-dark/80 max-w-2xl mx-auto mb-8">
            {currentCity
              ? `Explore ${stations.length} stations across ${lines.length} lines in ${currentCity.name}`
              : 'Your complete guide to metro rail travel across 23 Indian cities. Plan routes, calculate fares, and explore stations.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {selectedCity ? (
              <>
                <Link to="/route" className="btn-primary text-lg">
                  Plan a Route
                </Link>
                <Link to="/cities" className="btn-secondary text-lg">
                  Change City
                </Link>
              </>
            ) : (
              <>
                <Link to="/cities" className="btn-primary text-lg">
                  Select Your City
                </Link>
                <Link to="/about" className="btn-secondary text-lg">
                  Learn More
                </Link>
              </>
            )}
          </div>

          {/* Current City Badge */}
          {currentCity && (
            <div className="mt-8 inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm">
              <span className="w-3 h-3 rounded-full bg-metro-green"></span>
              <span className="font-medium">{currentCity.name}</span>
              <span className="text-neutral-dark/50">|</span>
              <span className="text-neutral-dark/70">{currentCity.authorityAbbr}</span>
            </div>
          )}
        </div>
      </section>

      {/* Banner Ad - After Hero */}
      <BannerAd
        slot={AD_SLOTS.HOME_HERO}
        format="horizontal"
        className="flex justify-center"
        wrapperClassName="py-6 bg-white container-responsive"
      />

      {/* Quick Actions */}
      <section className="py-12 lg:py-16">
        <div className="container-responsive">
          <h2 className="text-2xl lg:text-3xl 2xl:text-tv-2xl font-bold text-center mb-8">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickActions.map((action) => (
              <Link
                key={action.to}
                to={action.to}
                className={`card border-l-4 ${action.color} hover:scale-105 transition-transform tv-focusable`}
              >
                <span className="text-4xl mb-4 block">{action.icon}</span>
                <h3 className="text-xl font-semibold mb-2">{action.title}</h3>
                <p className="text-neutral-dark/70">{action.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby Section */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="container-responsive">
          <h2 className="text-2xl lg:text-3xl 2xl:text-tv-2xl font-bold text-center mb-8">
            Explore Nearby
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {nearbyOptions.map((option) => (
              <Link
                key={option.to}
                to={option.to}
                className="card text-center hover:bg-metro-blue-soft transition-colors tv-focusable"
              >
                <span className="text-3xl mb-2 block">{option.icon}</span>
                <span className="font-medium">{option.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 lg:py-16">
        <div className="container-responsive">
          <div className="bg-gradient-card rounded-2xl p-8 lg:p-12 text-white text-center">
            <h2 className="text-2xl lg:text-3xl font-bold mb-8">
              Covering All Major Metro Networks
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              <div>
                <div className="text-4xl lg:text-5xl font-bold">{operationalCities}</div>
                <div className="text-white/80">Cities</div>
              </div>
              <div>
                <div className="text-4xl lg:text-5xl font-bold">{totalStations}+</div>
                <div className="text-white/80">Stations</div>
              </div>
              <div>
                <div className="text-4xl lg:text-5xl font-bold">{totalLines}+</div>
                <div className="text-white/80">Metro Lines</div>
              </div>
              <div>
                <div className="text-4xl lg:text-5xl font-bold">1000+</div>
                <div className="text-white/80">KM Network</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Banner Ad - Before CTA */}
      <BannerAd
        slot={AD_SLOTS.HOME_FOOTER}
        format="rectangle"
        wrapperClassName="py-6 container-responsive flex justify-center"
      />

      {/* City Selection CTA */}
      {!selectedCity && (
        <section className="py-12 lg:py-16 bg-metro-blue-soft">
          <div className="container-responsive text-center">
            <h2 className="text-2xl lg:text-3xl font-bold mb-4">
              Ready to Explore?
            </h2>
            <p className="text-neutral-dark/70 mb-8 max-w-xl mx-auto">
              Select your city to get started with route planning, fare calculation, and more.
            </p>
            <Link to="/cities" className="btn-primary text-lg">
              Select Your City →
            </Link>
          </div>
        </section>
      )}
    </div>
  )
}

export default Home
